/* eslint-disable @typescript-eslint/no-misused-promises */
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, MenuItem, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import character from '../../assets/images/intake/step2/char.svg';
import { useUserAsaTaskHistoryContext } from '../../hooks/use-user-asa-task-history-context.hooks';
import { useAuthContext } from '../../hooks/use-user-auth-context.hooks';
import {
  AsaTaskName,
  AsaUserTasks,
  completeASATask,
  dispatchUpdateAsaTaskHistoryInfo,
} from '../../redux/slice/asa-task-history';
import { CustomUser } from '../../redux/slice/auth/CustomUser';
import {
  CareerRoadMapDto,
  StreuctureContextDto,
} from '../../redux/slice/career-roadmap/career-roadmap-type';
import { dispatchCareerRoadMapAssessment } from '../../redux/slice/career-roadmap/career-roadmap.operations';
import { roadMapQuestions } from '../../redux/slice/career-roadmap/careerRoadMapQuestions';
import { RootState, useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';
import { EnvironmentVariable } from '../../utils/constants';
import { buildASANextTaskUrl } from '../../utils/helper';
import { CircularLoader } from '../loader/CircularLoader';

interface ComponentProps {
  step: number;
  onBack: () => void;
}

interface FormData extends StreuctureContextDto {}

const CareerRoadMapSchema = yup
  .object({
    bilingual: yup.string().required('Please select a option'),
    reliableTransportation: yup.string().required('Please select a option'),
    caregivingDuties: yup.string().required('Please select a option'),
  })

  .required();

export const StructuredContext: React.FC<ComponentProps> = ({ step, onBack }) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const bilingulaState = useSelector(
    (state: RootState) => state.careerRoadmap.context_structured_bilingual,
  );
  const transportationState = useSelector(
    (state: RootState) => state.careerRoadmap.context_structured_transport,
  );
  const caregivingState = useSelector(
    (state: RootState) => state.careerRoadmap.context_structured_caregiving,
  );

  const currentUser: CustomUser | null = useAuthContext();
  const [currsentAsaTask, setCurrentAsaTask] = useState<AsaUserTasks | undefined>(undefined);
  const asaUserTaskHistory = useUserAsaTaskHistoryContext();

  useEffect(() => {
    if (
      currentUser?.claims?.isAsaUser &&
      Object.keys(asaUserTaskHistory).length &&
      asaUserTaskHistory?.tasks?.length
    ) {
      const currentTask = asaUserTaskHistory.tasks?.find(
        (x) => !x.isCompleted && x.taskName === AsaTaskName.CAREERROADMAP,
      );

      setCurrentAsaTask(currentTask);

      if (currentTask && !currentTask?.startDate) {
        void dispatch(
          dispatchUpdateAsaTaskHistoryInfo({
            asaUserId: currentUser?.claims?.asaUserId || 0,
            taskId: currentTask?.taskId || '',
            taskStep: currentTask?.taskStep || 1,
            startDate: currentTask?.startDate || new Date(),
            taskName: AsaTaskName.CAREERROADMAP,
          }),
        );
      }

      if (currsentAsaTask && (currsentAsaTask?.taskStep as number) != 16) {
        if (currsentAsaTask?.taskUrl) {
          const url = new URL(currsentAsaTask?.taskUrl || '');
          navigate(`${url.pathname}${url.search}`);
          // setIsDisabled(true);
        }
      }
    }
  }, [asaUserTaskHistory, currentUser, currsentAsaTask]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(CareerRoadMapSchema),
    defaultValues: {
      bilingual: bilingulaState?.answer ?? '',
      reliableTransportation: transportationState?.answer ?? '',
      caregivingDuties: caregivingState?.answer ?? '',
    },
  });

  const backButtonClick = () => {
    onBack();
  };
  const submitCareerRoadMapAnswer: SubmitHandler<FormData> = async (data: FormData) => {
    logger.debug('Submitted personal info.', data);
    setIsLoading(true);

    try {
      if (data) {
        const question18: CareerRoadMapDto = {
          id: roadMapQuestions[18].questionNumber,
          answer: data?.bilingual,
          question: roadMapQuestions[18].question,
        };
        if (!bilingulaState?.answer || bilingulaState.answer !== data.bilingual) {
          question18 &&
            (await dispatch(
              dispatchCareerRoadMapAssessment(question18, 'context_structured_bilingual'),
            ));
        }

        const question19: CareerRoadMapDto = {
          id: roadMapQuestions[19].questionNumber,
          answer: data?.reliableTransportation,
          question: roadMapQuestions[19].question,
        };
        if (
          !transportationState?.answer ||
          transportationState.answer !== data.reliableTransportation
        ) {
          question19 &&
            (await dispatch(
              dispatchCareerRoadMapAssessment(question19, 'context_structured_transport'),
            ));
        }

        const question20: CareerRoadMapDto = {
          id: roadMapQuestions[20].questionNumber,
          answer: data?.caregivingDuties,
          question: roadMapQuestions[20].question,
        };
        if (!caregivingState?.answer || caregivingState.answer !== data.bilingual) {
          question20 &&
            (await dispatch(
              dispatchCareerRoadMapAssessment(question20, 'context_structured_caregiving'),
            ));
        }

        if (currentUser?.claims?.isAsaUser && currsentAsaTask) {
          const endPointUrl = process.env[EnvironmentVariable.REACT_APP_ASA_COMPLETE_TASK_URL];
          const isStaging =
            process.env[EnvironmentVariable.REACT_APP_BASE_URL]?.includes('staging') ||
            process.env[EnvironmentVariable.REACT_APP_BASE_URL]?.includes('localhost');
          const taskResponse = await completeASATask(
            {
              asaUserId: currentUser?.claims?.asaUserId as number,
              taskId: currsentAsaTask?.taskId,
              taskStep: currsentAsaTask?.taskStep || step,
            },
            endPointUrl || '',
            isStaging || false,
          );

          if (!taskResponse || !taskResponse?.ok) {
            toast.error('Something went wrong!, Please try after sometime.');
            return;
          }

          let url = new URL(currsentAsaTask?.taskUrl || '');
          const nextTaskUrl = buildASANextTaskUrl(url, AsaTaskName.CAREERROADMAP, step + 1);
          url = new URL(nextTaskUrl || '');
          await dispatch(
            dispatchUpdateAsaTaskHistoryInfo({
              startDate: new Date(),
              asaUserId: currentUser?.claims?.asaUserId || 0,
              taskId: currsentAsaTask?.taskId || '',
              taskStep:
                (currsentAsaTask && parseInt(currsentAsaTask?.taskStep.toString()) + 1) || 16,
              taskUrl: nextTaskUrl,
              taskName: AsaTaskName.CAREERROADMAP,
              totalSteps: 17,
            }),
          );

          await dispatch(
            dispatchUpdateAsaTaskHistoryInfo({
              asaUserId: currentUser?.claims?.asaUserId || 0,
              taskId: currsentAsaTask?.taskId || '',
              taskStep: currsentAsaTask?.taskStep || step,
              isCompleted: true,
              completedDate: new Date(),
            }),
          );
          toast.success('Task has been successfully submitted');
          navigate(`${url.pathname}${url.search}`);
        } else {
          navigate(`/career-roadmap/step${step + 1}`);
        }
      }
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    reset({
      bilingual: bilingulaState?.answer,
      reliableTransportation: transportationState?.answer,
      caregivingDuties: caregivingState?.answer,
    });
  }, [reset, bilingulaState?.answer, transportationState?.answer, caregivingState?.answer]);

  useEffect(() => {
    window.scrollTo(0, 0);
  });

  return (
    <div>
      {isLoading && <CircularLoader />}

      <div className="flex mt-9 mb-10 md:px-10">
        <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">
          {roadMapQuestions[18].questionNumber}
        </div>
        <div>
          <h4 className="text-2xl">
            <span className="font-semibold">Quick Toggles </span>
          </h4>
        </div>
      </div>
      <div className="character absolute bottom-0 right-[-85px] lg:right-[-138px]">
        <img className="" src={character} alt="" />
      </div>
      <div className="flex mt-9 mb-10 md:px-10">
        <Box
          className="w-full"
          component="form"
          sx={{ '& > :not(style)': { mb: 4 } }}
          autoComplete="off"
          onSubmit={handleSubmit(submitCareerRoadMapAnswer)}
        >
          <Controller
            control={control}
            name="bilingual"
            render={({ field }) => (
              <TextField
                select
                fullWidth
                label={roadMapQuestions[18]?.question}
                value={field.value}
                onChange={field.onChange}
                required
                error={!!errors.bilingual}
                helperText={errors.bilingual?.message}
              >
                <MenuItem value="">Select</MenuItem>
                {roadMapQuestions[18]?.options.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="reliableTransportation"
            render={({ field }) => (
              <TextField
                select
                fullWidth
                label={roadMapQuestions[19]?.question}
                value={field.value}
                onChange={field.onChange}
                required
                error={!!errors.reliableTransportation}
                helperText={errors.reliableTransportation?.message}
              >
                <MenuItem value="">Select</MenuItem>
                {roadMapQuestions[19]?.options.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="caregivingDuties"
            render={({ field }) => (
              <TextField
                select
                fullWidth
                label={roadMapQuestions[20]?.question}
                value={field.value}
                onChange={field.onChange}
                required
                error={!!errors.caregivingDuties}
                helperText={errors.caregivingDuties?.message}
              >
                <MenuItem value="">Select</MenuItem>
                {roadMapQuestions[20]?.options.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <div className="flex justify-end lg:justify-between lg:mt-20 mt-10">
            <button onClick={handleSubmit(backButtonClick)} className="btn-internal" type="button">
              Back
            </button>
            <button className="btn-internal ml-5" disabled={isLoading} type="submit">
              Next
            </button>
          </div>
        </Box>
      </div>
    </div>
  );
};
